import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { ShieldCheckIcon, CalendarIcon, CheckCircleIcon, TargetIcon, TrophyIcon, LightbulbIcon, UsersIcon, BriefcaseIcon, HandshakeIcon } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
function AnimatedCounter({
  from,
  to,
  duration = 2,
  suffix = ''
}: {
  from: number;
  to: number;
  duration?: number;
  suffix?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const count = useMotionValue(from);
  const rounded = useTransform(count, latest => Math.round(latest) + suffix);
  useEffect(() => {
    if (inView) {
      animate(count, to, {
        duration
      });
    }
  }, [count, inView, to, duration]);
  return <motion.span ref={ref} data-id="element-279" data-mp="2evxzmn7wocam">{rounded}</motion.span>;
}
export function AboutPage() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash === '#contracts') {
      const element = document.getElementById('contracts');
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }
  }, [location]);
  return <div className="w-full" data-id="element-280" data-mp="2yk6qmtxpqc8j">
      {/* HERO SECTION */}
      <section className="section-navy hero-grid-pattern relative overflow-hidden" data-id="element-281" data-mp="2ykqaig6xbap7">
        {/* Tech/Circuit SVG Pattern Overlay */}
        <div className="absolute inset-0 opacity-5 pointer-events-none" data-id="element-282" data-mp="2ouev9xhshf30">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-283" data-mp="2ou54w8su15ws">
            <defs data-id="element-284" data-mp="2ts6xbkc5ehkx">
              <pattern id="hexagons" width="50" height="43.4" patternUnits="userSpaceOnUse" patternTransform="scale(2)" data-id="element-285" data-mp="2trnhqa9z05a2">
                <path d="M25 0 L50 14.4 L50 43.3 L25 57.7 L0 43.3 L0 14.4 Z" fill="none" stroke="#FFFFFF" strokeWidth="1" data-id="element-286" data-mp="3hh828xlrvalg" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hexagons)" data-id="element-287" data-mp="3w59fim33hh98" />
          </svg>
        </div>

        {/* Decorative SVG Shield Graphic */}
        <div className="absolute right-10 top-1/2 -translate-y-1/2 opacity-20 hidden lg:block pointer-events-none" data-id="element-288" data-mp="3m7br9yl8jkgm">
          <svg width="280" height="320" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="0.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-289" data-mp="3h90kjwq8lq2m">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-290" data-mp="3d903037ucyrc" />
            <circle cx="12" cy="10" r="3" data-id="element-291" data-mp="38ff3g6qlnwq1" />
            <path d="M12 13v4" data-id="element-292" data-mp="38ecbrwlgdq92" />
            <path d="M10 15h4" data-id="element-293" data-mp="33gav1ibi230r" />
          </svg>
        </div>

        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center relative z-10" data-id="element-294" data-mp="33erc1jygldva">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} data-id="element-295" data-mp="2yg6drq2rp49h">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight max-w-4xl mx-auto text-white" data-id="element-296" data-mp="2ti4t50pp0wvw">
              About New River Systems
            </h1>
            <div className="h-1.5 w-24 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-297" data-mp="1501yonnf6ml5"></div>
            <p className="text-xl text-nrs-light font-bold mb-3 max-w-2xl mx-auto" data-id="element-298" data-mp="190cfkvcr2xes">
              Delivering Mission-Critical IT Solutions Since 2007
            </p>
            <p className="text-sm text-white/60 font-medium mb-8 max-w-2xl mx-auto tracking-wide uppercase" data-id="element-299" data-mp="1dy5ldna55pej">
              Trusted by DIA & Federal Agencies | Cleared Teams | 19 Years
              Excellence
            </p>

            {/* Inline Stats Pills */}
            <div className="flex flex-wrap justify-center gap-3 mt-4" data-id="element-300" data-mp="qb5ux2sebvqc">
              <span className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm font-semibold text-white backdrop-blur-sm" data-id="element-301" data-mp="v8z29z5fqvs7">
                95%+ Client Retention
              </span>
              <span className="px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm font-semibold text-white backdrop-blur-sm" data-id="element-302" data-mp="2lla0ecomoef">
                100% Cleared Workforce
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="section-white" data-id="element-303" data-mp="2580caosa36q6">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-304" data-mp="2591mkr1gzp6u">
          <h2 className="text-3xl font-bold mb-4 text-center" data-id="element-305" data-mp="2izx1ol2kb2iv">Our Story</h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-306" data-mp="2e8exlo58slto"></div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start" data-id="element-307" data-mp="2sx1dbqys7flf">
            {/* Left Column - Story Text */}
            <div className="lg:col-span-7 space-y-6 text-lg text-nrs-gray leading-relaxed" data-id="element-308" data-mp="2nyrijhgyyh0c">
              <p data-id="element-309" data-mp="31y9au6th4ceg">
                Founded in March 2007 by Edward Albers, New River Systems
                Corporation has grown from a small IT services provider to a
                trusted partner for federal government agencies and enterprises.
              </p>
              <p data-id="element-310" data-mp="17ljgxp33v372">
                With deep expertise in ServiceNow platform stewardship, identity
                and access management, and mission-critical program delivery, we
                serve clients who demand both technical excellence and security
                clearance.
              </p>
              <p data-id="element-311" data-mp="35fnac75zgpe4">
                Our team of cleared professionals brings decades of combined
                experience to every engagement, ensuring mission success in
                secure government environments.
              </p>
              <p data-id="element-312" data-mp="2vh7rerh40mfp">
                <strong className="text-nrs-navy" data-id="element-313" data-mp="2vgpm75cgyxvn">Our Approach:</strong> What
                sets New River Systems apart is our commitment to self-governing
                excellence. When we provide both governance and development
                services, we apply the same rigorous standards to our own work
                that we enforce for our clients. This eliminates the vendor
                conflicts and principal-agent problems that plague traditional
                consulting engagements.
              </p>
              <p data-id="element-314" data-mp="oox90352u8oh">
                <strong className="text-nrs-navy" data-id="element-315" data-mp="1xnyhk76kj7jx">Our Culture:</strong> We have
                built a culture of mission-focused professionals who understand
                that our work directly impacts national security. Every team
                member undergoes rigorous background investigations and
                maintains active security clearances, ensuring we can operate in
                the most sensitive environments federal agencies and enterprises
                require.
              </p>
              <p data-id="element-316" data-mp="2cd4etpluinp6">
                <strong className="text-nrs-navy" data-id="element-317" data-mp="2ccm81z1k4r30">Looking Forward:</strong> As
                federal IT modernization accelerates, we continue investing in
                our team expertise across emerging technologies—Zero Trust
                Architecture, cloud-native platforms, and AI-powered governance
                tools—while maintaining the deep domain knowledge and cleared
                workforce that have made us a trusted partner for 19 years.
              </p>
            </div>

            {/* Right Column - Timeline */}
            <div className="lg:col-span-5 bg-nrs-light/50 p-8 rounded-xl border border-gray-100" data-id="element-318" data-mp="32ozfdpubdosh">
              <h3 className="text-2xl font-bold mb-8 text-nrs-navy" data-id="element-319" data-mp="37o28qd0xm6km">
                Our Journey
              </h3>
              <div className="relative border-l-2 border-nrs-orange ml-3 space-y-8 py-2" data-id="element-320" data-mp="3gmnzgvgk4rwh">
                {[{
                year: '2007',
                title: 'Founded by Edward Albers'
              }, {
                year: '2010',
                title: 'GSA MAS Contract Awarded'
              }, {
                year: '2020',
                title: 'DIA SNOE I Prime Contract Awarded'
              }, {
                year: '2024',
                title: 'Continued Growth & Expansion'
              }, {
                year: '2026',
                title: 'DIA SNOE II Recompete Won'
              }, {
                year: '2026',
                title: 'CMMI Level 3 Recertification Achieved'
              }].map((item, idx) => <div key={idx} className="relative pl-8" data-id="element-321" data-mp="yg2wkmam95g8" data-mpi={idx}>
                    <div className="absolute -left-[9px] top-1.5 h-4 w-4 rounded-full bg-nrs-orange border-4 border-white shadow-sm" data-id="element-322" data-mp="yip5oqd5nt0k"></div>
                    <div className="font-bold text-nrs-orange text-xl mb-1" data-id="element-323" data-mp="18fbpdbtqwe10">
                      {item.year}
                    </div>
                    <div className="text-nrs-navy font-semibold" data-id="element-324" data-mp="13gqln2fabc7t">
                      {item.title}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT SETS US APART */}
      <section className="section-light-blue" data-id="element-325" data-mp="pgfya9d6ofre">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-326" data-mp="piai1kcpy1tw">
          <h2 className="text-3xl font-bold mb-4 text-center" data-id="element-327" data-mp="knwid5r0x8jf">
            What Sets Us Apart
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-12" data-id="element-328" data-mp="3t3g7ptokq6t7"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-329" data-mp="3o5eptc2vwdjb">
            <div className="card text-center flex flex-col items-center border-b-[3px] border-transparent hover:border-b-nrs-orange" data-id="element-330" data-mp="23nvto5wx7cwa">
              <div className="icon-circle mb-4" data-id="element-331" data-mp="28g6sf5fxcsn9">
                <ShieldCheckIcon className="h-8 w-8 text-nrs-orange" data-id="element-332" data-mp="1tqhkmrdbtqe0" />
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-333" data-mp="1yosuv37ja2wr">Cleared Teams</h3>
              <p className="text-nrs-gray" data-id="element-334" data-mp="1ymzkpdxpp0k0">
                Cleared workforce for secure government environments
              </p>
            </div>
            <div className="card text-center flex flex-col items-center border-b-[3px] border-transparent hover:border-b-nrs-orange" data-id="element-335" data-mp="2rdzr1vjimgeu">
              <div className="icon-circle mb-4" data-id="element-336" data-mp="2ddp3p2hezjyf">
                <CalendarIcon className="h-8 w-8 text-nrs-orange" data-id="element-337" data-mp="2hf0tb80ag56m" />
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-338" data-mp="11z1r0ddcr3ea">
                Deep Federal Experience
              </h3>
              <p className="text-nrs-gray" data-id="element-339" data-mp="160wsbhva9gs2">
                19 years serving DoD and federal clients
              </p>
            </div>
            <div className="card text-center flex flex-col items-center border-b-[3px] border-transparent hover:border-b-nrs-orange" data-id="element-340" data-mp="1qo3y7l9cv4ko">
              <div className="icon-circle mb-4" data-id="element-341" data-mp="1hv8ren48p0ss">
                <CheckCircleIcon className="h-8 w-8 text-nrs-orange" data-id="element-342" data-mp="1huh6q7c6knha" />
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-343" data-mp="1lvsojqotc4ex">
                Self-Governing Excellence
              </h3>
              <p className="text-nrs-gray" data-id="element-344" data-mp="17wb39laq79ab">
                We govern what we build - eliminating vendor conflicts
              </p>
            </div>
            <div className="card text-center flex flex-col items-center border-b-[3px] border-transparent hover:border-b-nrs-orange" data-id="element-345" data-mp="ywefdlw5uegb">
              <div className="icon-circle mb-4" data-id="element-346" data-mp="12z16fkl0bvcg">
                <TargetIcon className="h-8 w-8 text-nrs-orange" data-id="element-347" data-mp="12zt2sxmfhx3s" />
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-348" data-mp="2t8b77fyilmbw">
                Mission-Critical Delivery
              </h3>
              <p className="text-nrs-gray" data-id="element-349" data-mp="2x9obvmpvfjh8">
                Trusted partner for federal defense and civilian agencies
              </p>
            </div>
            <div className="card text-center flex flex-col items-center border-b-[3px] border-transparent hover:border-b-nrs-orange" data-id="element-350" data-mp="hodeflmi9xgv">
              <div className="icon-circle mb-4" data-id="element-351" data-mp="vgjs9126rsd5">
                <TrophyIcon className="h-8 w-8 text-nrs-orange" data-id="element-352" data-mp="qnifozpgc4wq" />
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-353" data-mp="15c519j5o9swe">Proven Track Record</h3>
              <p className="text-nrs-gray" data-id="element-354" data-mp="10dk2zp9zdjal">
                Trusted by DIA and federal agencies for mission-critical
                programs
              </p>
            </div>
            <div className="card text-center flex flex-col items-center border-b-[3px] border-transparent hover:border-b-nrs-orange" data-id="element-355" data-mp="1eb8motsbake2">
              <div className="icon-circle mb-4" data-id="element-356" data-mp="3g4fyht8pujv7">
                <LightbulbIcon className="h-8 w-8 text-nrs-orange" data-id="element-357" data-mp="3g57u342bchlh" />
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-358" data-mp="3b6wkmufxk93s">Innovation in Security</h3>
              <p className="text-nrs-gray" data-id="element-359" data-mp="1f39oruw088ar">
                Leading-edge Zero Trust, ICAM, and platform governance solutions
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* STATISTICS WITH ANIMATED COUNTERS */}
      <section className="section-navy" data-id="element-360" data-mp="1p30n67nor7g4">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-361" data-mp="1t2hsn13444la">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-0 text-center" data-id="element-362" data-mp="1y1cds9ojy58k">
            <div className="lg:border-r lg:border-white/20 px-2" data-id="element-363" data-mp="1xzhxx9s513bc">
              <CalendarIcon className="h-8 w-8 text-white/50 mx-auto mb-3" data-id="element-364" data-mp="22yuni2ae8ohm" />
              <div className="text-4xl font-bold text-nrs-orange mb-2" data-id="element-365" data-mp="27s6xb6aeyqqb">
                <AnimatedCounter from={0} to={19} suffix="" data-id="element-366" data-mp="2cpz0iwgidz9t" />
              </div>
              <div className="text-xs md:text-sm text-nrs-light font-bold uppercase tracking-wider" data-id="element-367" data-mp="2cs1s3kxb80gn">
                Years in Business
              </div>
            </div>
            <div className="lg:border-r lg:border-white/20 px-2" data-id="element-368" data-mp="2lr6n4h9nxbo2">
              <HandshakeIcon className="h-8 w-8 text-white/50 mx-auto mb-3" data-id="element-369" data-mp="a4mmsnarj7at" />
              <div className="text-4xl font-bold text-nrs-orange mb-2" data-id="element-370" data-mp="54qkqtlmlpxw">
                <AnimatedCounter from={0} to={95} suffix="%+" data-id="element-371" data-mp="56tijzt0snd6" />
              </div>
              <div className="text-xs md:text-sm text-nrs-light font-bold uppercase tracking-wider" data-id="element-372" data-mp="1359fcoqpjpt">
                Client Retention
              </div>
            </div>
            <div className="lg:border-r lg:border-white/20 px-2" data-id="element-373" data-mp="st01hh3zfni6">
              <ShieldCheckIcon className="h-8 w-8 text-white/50 mx-auto mb-3" data-id="element-374" data-mp="ospvw5k3qz3u" />
              <div className="text-4xl font-bold text-nrs-orange mb-2" data-id="element-375" data-mp="outymk69ls2j">
                <AnimatedCounter from={0} to={100} suffix="%" data-id="element-376" data-mp="jzwpzecwdkq6" />
              </div>
              <div className="text-xs md:text-sm text-nrs-light font-bold uppercase tracking-wider" data-id="element-377" data-mp="f1v7ovnappfr">
                Cleared Workforce
              </div>
            </div>
            <div className="px-2" data-id="element-378" data-mp="17qsg871pg7vb">
              <BriefcaseIcon className="h-8 w-8 text-white/50 mx-auto mb-3" data-id="element-379" data-mp="17pr37x14pbay" />
              <div className="text-4xl font-bold text-nrs-orange mb-2" data-id="element-380" data-mp="rarkpr8nknyh">
                <AnimatedCounter from={0} to={10} suffix="+" data-id="element-381" data-mp="wannxo9j0bcz" />
              </div>
              <div className="text-xs md:text-sm text-nrs-light font-bold uppercase tracking-wider" data-id="element-382" data-mp="ib4jp6ju5k5y">
                Active Programs
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTRACT VEHICLES */}
      <section id="contracts" className="section-white" data-id="element-383" data-mp="1b0v2vu0brc8x">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15" data-id="element-384" data-mp="1f1dr7e30e06d">
          <h2 className="text-3xl font-bold mb-4 text-center" data-id="element-385" data-mp="1f3hv3w0wqz6n">
            Contract Vehicles
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-6" data-id="element-386" data-mp="11861xt2ehgru"></div>
          <p className="text-center text-lg text-nrs-gray mb-8 max-w-2xl mx-auto" data-id="element-387" data-mp="1678y0o0ekcno">
            New River Systems holds multiple contract vehicles to streamline
            procurement for federal clients, including DLA JETS 2.0, GSA MAS,
            GSA Alliant SB, DIA SITE III, NGA EMERALD,{' '}
            <Link to="/about-us/contract-vehicles/nasa-sewp-vi" className="font-semibold text-nrs-navy underline underline-offset-2 hover:text-nrs-orange" data-id="element-388" data-mp="282dcalo68l1g">
              NASA SEWP VI
            </Link>
            , and VETS 2.
          </p>

          <div className="text-center mb-12" data-id="element-389" data-mp="2cv6hfanjxve2">
            <Link to="/about-us/contract-vehicles" className="btn-primary" data-id="element-390" data-mp="2cvg5utsy7uhn">
              View All Contract Vehicles
            </Link>
          </div>

          <div className="max-w-3xl mx-auto bg-nrs-light p-6 rounded-md text-sm text-nrs-gray grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-391" data-mp="1l40dsk8mhfk1">
            <div data-id="element-392" data-mp="1z4b15daq4c0g">
              <p className="mb-2" data-id="element-393" data-mp="1z4t9h8anugoq">
                <span className="font-bold text-nrs-navy" data-id="element-394" data-mp="1u4op376mt1t8">Primary NAICS:</span>{' '}
                541519 - Other Computer Related Services
              </p>
              <p className="mb-2" data-id="element-395" data-mp="3agu06sr34f74">
                <span className="font-bold text-nrs-navy" data-id="element-396" data-mp="35jke9n2wmlo1">
                  Other Associated NAICS:
                </span>{' '}
                518210, 511199
              </p>
              <p data-id="element-397" data-mp="3dmcjs3dfkiib">
                <span className="font-bold text-nrs-navy" data-id="element-398" data-mp="3hlv3ivtimnjk">
                  Business Classifications:
                </span>{' '}
                SBA Certified Service-Disabled Veteran Owned Small Business
              </p>
            </div>
            <div data-id="element-399" data-mp="3rizura27x978">
              <p className="mb-2" data-id="element-400" data-mp="3rjj2xz22gj82">
                <span className="font-bold text-nrs-navy" data-id="element-401" data-mp="2twamwjku42dg">CAGE Code:</span>{' '}
                4RNU2
              </p>
              <p className="mb-2" data-id="element-402" data-mp="2yuuaeof2tm7x">
                <span className="font-bold text-nrs-navy" data-id="element-403" data-mp="2yvwy6nh3r8jm">UEI:</span>{' '}
                DWT1XD8JT8M4
              </p>
              <p className="mb-2" data-id="element-404" data-mp="33sdl5eckfgv4">
                <span className="font-bold text-nrs-navy" data-id="element-405" data-mp="38kx2mmediw57">DUNS:</span> 799154450
              </p>
              <p data-id="element-406" data-mp="2b62k62l1rdxs">
                <span className="font-bold text-nrs-navy" data-id="element-407" data-mp="2b6kqxt5c5ajy">SAM Registered:</span>{' '}
                Active
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-light-blue" data-id="element-408" data-mp="28kbo0bxlcgmj">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-409" data-mp="28j0lsgjsngxj">
          <h3 className="text-2xl font-bold mb-8" data-id="element-410" data-mp="23n1o22cksmkg">
            Ready to Work with New River Systems?
          </h3>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-411" data-mp="1yp0a1vu0bdfu">
            <Link to="/contact-us" className="btn-primary" data-id="element-412" data-mp="1tnudto15181k">
              Contact Business Development
            </Link>
            <Link to="/about-us/contract-vehicles" className="btn-secondary" data-id="element-413" data-mp="1pobv8ywsh91w">
              View Contract Vehicles
            </Link>
          </div>
        </div>
      </section>
    </div>;
}